import { FC, useContext, useMemo, useState } from "react";
import { Container, Box, Typography, Grid, Tabs, Tab } from "@mui/material";
import { FloatingCard } from "src/components/molecules/assessment/FloatingCard";
import { AssessmentContext } from "src/context/AssessmentContext";
import { MsdDto } from "src/app/services/generatedApi";
import { defaultMsdData } from "src/app/logic/assessment/msd/constants";
import { calculateMsdScore } from "src/app/logic/assessment/msd/score";
import { MSDScoreBoard } from "./MSDScoreBoard";
import { MsdBodyZones } from "./MsdBodyZones";
import { LiftingAnalysis } from "./LiftingAnalysis/LiftingAnalysis";
import { HandArmVibrationAnalysis } from "./HandArmVibrationAnalysis/HandArmVibrationAnalysis";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`msd-tabpanel-${index}`}
      aria-labelledby={`msd-tab-${index}`}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

function a11yProps(index: number) {
  return {
    id: `msd-tab-${index}`,
    "aria-controls": `msd-tabpanel-${index}`,
  };
}

type MSDPageProps = {
  fileName?: string;
  havePose: boolean;
};

export const MSDPage: FC<MSDPageProps> = ({ fileName, havePose }) => {
  const [tabValue, setTabValue] = useState(0);
  const { assessmentDataDto, setAssessmentDataDto } =
    useContext(AssessmentContext);

  const scores = useMemo(() => {
    if (!assessmentDataDto?.msd) return null;
    return calculateMsdScore(assessmentDataDto.msd);
  }, [assessmentDataDto?.msd]);

  const updateMsd = (newMsd: MsdDto) => {
    setAssessmentDataDto({ msd: newMsd });
  };

  const handleUpdate = (
    bodyZone: keyof MsdDto,
    category: string,
    propertyName: string,
    value: boolean,
  ) => {
    if (!assessmentDataDto?.msd) {
      const newMsd = JSON.parse(JSON.stringify(defaultMsdData)) as MsdDto;

      (
        newMsd[bodyZone]![
          category as keyof (typeof newMsd)[typeof bodyZone]
        ] as any
      )[propertyName] = value;

      if (value) {
        const categoryData = newMsd[bodyZone]![
          category as keyof (typeof newMsd)[typeof bodyZone]
        ] as any;
        const basePropertyName = propertyName.split("_")[0];

        Object.keys(categoryData).forEach((key) => {
          if (key.startsWith(basePropertyName + "_") && key !== propertyName) {
            categoryData[key] = false;
          }
        });
      }

      updateMsd(newMsd);
      return;
    }

    const newMsd = JSON.parse(JSON.stringify(assessmentDataDto.msd)) as MsdDto;

    const categoryData = newMsd[bodyZone]![
      category as keyof (typeof newMsd)[typeof bodyZone]
    ] as any;

    categoryData[propertyName] = value;

    if (value) {
      const basePropertyName = propertyName.split("_")[0];

      Object.keys(categoryData).forEach((key) => {
        if (key.startsWith(basePropertyName + "_") && key !== propertyName) {
          categoryData[key] = false;
        }
      });
    }

    updateMsd(newMsd);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {fileName && <FloatingCard fileName={fileName} havePose={havePose} />}

      <Typography variant="h4" gutterBottom>
        MSD Screening Assessment
      </Typography>

      {scores && (
        <Box mb={4}>
          <Grid container spacing={3}>
            <MSDScoreBoard scores={scores} />
          </Grid>
        </Box>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="MSD assessment tabs"
          sx={{
            "& .MuiTab-root": {
              fontWeight: "bold",
              fontSize: "0.95rem",
            },
          }}
        >
          <Tab label="Body Zone Assessment" {...a11yProps(0)} />
          <Tab label="Lifting Analysis" {...a11yProps(1)} />
          <Tab label="Hand-Arm Vibration" {...a11yProps(2)} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <MsdBodyZones
          msdData={assessmentDataDto?.msd}
          handleUpdate={handleUpdate}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <LiftingAnalysis />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <HandArmVibrationAnalysis />
      </TabPanel>
    </Container>
  );
};

export default MSDPage;
